/**
 * tokenStorage.js — Centralized JWT token storage (hardened).
 *
 * SECURITY NOTES — ★ Fase 59.P1 (migrazione da localStorage)
 * ──────────────────────────────────────────────────────────
 * Il token NON è più persistito in `localStorage` (superficie XSS + persiste
 * su disco a browser chiuso). Nuova strategia:
 *
 *   1. **In-memory first**: il token vive in una variabile di modulo; le
 *      letture (interceptor axios) non toccano mai lo storage.
 *   2. **sessionStorage** come persistenza di riserva per sopravvivere al
 *      refresh della pagina (F5). Viene cancellato alla chiusura della tab.
 *   3. **BroadcastChannel ('gp_auth')** per sincronizzare le tab:
 *        • logout in una tab → logout ovunque
 *        • login in una tab → sessione condivisa nelle altre tab
 *        • una tab nuova (sessionStorage vuoto) chiede il token alle
 *          tab già autenticate (session handoff) → nessun re-login.
 *   4. **Migrazione legacy trasparente**: al primo load, un token ancora
 *      presente nella vecchia chiave `localStorage.gp_token` viene adottato
 *      in sessionStorage e RIMOSSO da localStorage (niente logout forzato).
 *   5. Controllo `exp` lato client (decode-only) con margine di 30s.
 *
 * Il passo successivo (fuori scope MVP) resta il cookie httpOnly lato server.
 *
 * PUBLIC API (invariata rispetto alla versione precedente)
 * ─────────────────────────────────────────────────────────
 *   getToken()          → string | null (null se scaduto)
 *   setToken(token)     → void
 *   clearToken()        → void
 *   getRawToken()       → string | null (senza check scadenza — debug)
 *   isTokenExpired(t?)  → boolean
 *   getTokenExp(t?)     → number | null (secondi epoch)
 *   onTokenChange(cb)   → unsubscribe fn (fires su set/clear da altre tab)
 */

const STORAGE_KEY = 'gp_token';
const LEGACY_LOCALSTORAGE_KEY = 'gp_token';
const CHANNEL_NAME = 'gp_auth';
const EXPIRATION_SKEW_SECONDS = 30; // treat token as expired 30s before real exp

// In-memory cache — single source of truth at runtime.
let cachedToken = null;
let cachedInitialized = false;

// Cross-tab listeners registered via onTokenChange().
const listeners = new Set();

// ─────────────────────────────────────────────────────────────────────────
// Storage helpers (sessionStorage + legacy localStorage migration)
// ─────────────────────────────────────────────────────────────────────────

function safeSessionGet() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage.getItem(STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

function safeSessionSet(value) {
  try {
    if (typeof window === 'undefined') return;
    if (value == null) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* storage may be unavailable in private mode — no-op */
  }
}

/** Adotta e rimuove un token rimasto nella vecchia chiave localStorage. */
function migrateLegacyToken() {
  try {
    if (typeof window === 'undefined') return null;
    const legacy = window.localStorage.getItem(LEGACY_LOCALSTORAGE_KEY);
    if (legacy) {
      window.localStorage.removeItem(LEGACY_LOCALSTORAGE_KEY);
      safeSessionSet(legacy);
      return legacy;
    }
  } catch {
    /* no-op */
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// Cross-tab sync via BroadcastChannel (session handoff + logout globale)
// ─────────────────────────────────────────────────────────────────────────

let channel = null;

function notifyListeners(newToken) {
  listeners.forEach((cb) => {
    try { cb(newToken); } catch { /* listener error — isolate */ }
  });
}

function getChannel() {
  if (channel !== null) return channel;
  try {
    if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') {
      channel = false; // marker: non disponibile (fallback = solo sessionStorage)
      return channel;
    }
    channel = new window.BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
      const msg = event?.data || {};
      if (msg.type === 'set' && typeof msg.token === 'string') {
        cachedToken = msg.token;
        cachedInitialized = true;
        safeSessionSet(cachedToken);
        notifyListeners(cachedToken);
      } else if (msg.type === 'clear') {
        cachedToken = null;
        cachedInitialized = true;
        safeSessionSet(null);
        notifyListeners(null);
      } else if (msg.type === 'request') {
        // Un'altra tab (nuova) chiede la sessione: rispondi se autenticati.
        if (cachedToken && !isTokenExpired(cachedToken)) {
          try { channel.postMessage({ type: 'set', token: cachedToken }); } catch { /* no-op */ }
        }
      }
    };
  } catch {
    channel = false;
  }
  return channel;
}

function broadcast(msg) {
  const ch = getChannel();
  if (!ch) return;
  try { ch.postMessage(msg); } catch { /* no-op */ }
}

// ─────────────────────────────────────────────────────────────────────────
// JWT helpers
// ─────────────────────────────────────────────────────────────────────────

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // Base64URL → Base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === 'function'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('binary');
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function initCache() {
  if (cachedInitialized) return;
  cachedInitialized = true;
  cachedToken = safeSessionGet() || migrateLegacyToken();
  // Attiva il canale cross-tab e, se questa tab è "vuota", chiedi la sessione
  // alle altre tab già autenticate (handoff asincrono → onTokenChange).
  getChannel();
  if (!cachedToken) broadcast({ type: 'request' });
}

// ─────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────

export function getRawToken() {
  initCache();
  return cachedToken || null;
}

export function getTokenExp(token) {
  const t = token ?? getRawToken();
  const payload = decodeJwtPayload(t);
  const exp = payload && typeof payload.exp === 'number' ? payload.exp : null;
  return exp;
}

export function isTokenExpired(token) {
  const exp = getTokenExp(token);
  if (!exp) return false; // no exp claim → cannot determine, assume live
  const nowSec = Math.floor(Date.now() / 1000);
  return nowSec + EXPIRATION_SKEW_SECONDS >= exp;
}

export function getToken() {
  initCache();
  if (!cachedToken) return null;
  if (isTokenExpired(cachedToken)) {
    // Auto-clear expired tokens on read
    clearToken();
    return null;
  }
  return cachedToken;
}

export function setToken(token) {
  cachedToken = token || null;
  cachedInitialized = true;
  safeSessionSet(cachedToken);
  // Difesa in profondità: mai lasciare copie del token nella chiave legacy.
  try { window.localStorage.removeItem(LEGACY_LOCALSTORAGE_KEY); } catch { /* no-op */ }
  if (cachedToken) broadcast({ type: 'set', token: cachedToken });
  else broadcast({ type: 'clear' });
}

export function clearToken() {
  cachedToken = null;
  cachedInitialized = true;
  safeSessionSet(null);
  // Difesa in profondità: rimuovi anche l'eventuale residuo legacy.
  try { window.localStorage.removeItem(LEGACY_LOCALSTORAGE_KEY); } catch { /* no-op */ }
  broadcast({ type: 'clear' });
}

/**
 * Subscribe ai cambi token provenienti dalle ALTRE tab (set e clear).
 * Il callback riceve il nuovo token (string) o null (logout).
 *
 * @param {(newToken: string|null) => void} cb
 * @returns {() => void} unsubscribe fn
 */
export function onTokenChange(cb) {
  if (typeof cb !== 'function') return () => {};
  initCache();
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Named default export for convenience.
const tokenStorage = {
  getToken,
  setToken,
  clearToken,
  getRawToken,
  isTokenExpired,
  getTokenExp,
  onTokenChange,
};

export default tokenStorage;
